/** Error codes returned by the backend API */
export enum ErrorCodesDto {
    UNKNOWN = 'unknown',
    RESOURCE_NOT_FOUND = 'resource_not_found',
    // Also a 404, and deliberately distinct: the authenticated identity no longer exists in the
    // auth server, as opposed to some resource it asked for (a mistyped voucher answers with
    // RESOURCE_NOT_FOUND on the same endpoint).
    AUTH_SERVER_USER_NOT_FOUND = 'auth_server_user_not_found',
    // The auth-server account is live but nobody ever proved control of its address, so it cannot
    // key an account. Not retryable: the address has to be verified first.
    AUTH_SERVER_EMAIL_UNVERIFIED = 'auth_server_email_unverified',
    // The other 409 completing a Google sign-up: a different account already holds the address, so
    // this identity has none and never will under it.
    EMAIL_ALREADY_REGISTERED = 'email_already_registered',
    // The opposite outcome to the one above, one word apart: this identity already has its own Balm
    // account, so the only thing left to do is let it in. Raised by the guard and by the key
    // collision two submissions race into, which mean the same thing (IPD-3078).
    USER_ALREADY_REGISTERED = 'user_already_registered',
    UNAUTHORIZED = 'unauthorized',
    BAD_REQUEST = 'bad_request',
    CONFLICT = 'conflict',
    MISMATCH_INTERVENTION = 'mismatch_intervention',
    MISMATCH_SESSION = 'mismatch_session',
    MISSING_QUESTION_ANSWERED = 'missing_question_answered',
    INVALID_AGE = 'invalid_age',
    DUPLICATED_EMAIL = 'duplicated_email',
    MISMATCH_TOS_VERSION = 'mismatch_tos_version',
    METHOD_ARGUMENT_TYPE_MISMATCH = 'method_argument_type_mismatch',
    INVALID_MARKETING_CODE = 'invalid_marketing_code',
    INVALID_PASSWORD = 'invalid_password',
    PASSWORD_CHANGE_ISSUE = 'password_change_issue',
    INVALID_VERIFICATION_CODE = 'invalid_verification_code',
    PASSWORD_POLICY_VIOLATION = 'password_policy_violation',
    CONSENT_REQUIRED = 'consent_required',
    CONSENT_NEEDS_REAPPROVAL = 'consent_needs_reapproval',
    NO_ACTIVE_LICENSES = 'no_active_licenses',
    ACTIVE_SUBSCRIPTION_EXISTS = 'active_subscription_exists',
    INVALID_VOUCHER_CODE = 'invalid_voucher_code',
    VOUCHER_RENEWAL_INVALID = 'voucher_renewal_invalid',
    INVALID_COUNTRY = 'invalid_country',
    MULTIPLE_QUESTIONNAIRE_ANSWERING_ATTEMPT = 'multiple_questionnaire_answering_attempt',
    FORBIDDEN = 'forbidden',
    CAPTCHA_ERROR = 'captcha_error',
    CAPTCHA_NOT_COMPLETED = 'captcha_not_completed',
    CAPTCHA_SCRIPT_TIMEOUT = 'captcha_script_timeout',
    DELETE_USER_ERROR = 'delete_user_error',
    ENROLLED_IN_A_INVALID_INTERVENTION = 'enrolled_in_a_invalid_intervention',
    // Backend codes that reuse existing translations (defined here for documentation)
    NOT_SUPPORTED_COUNTRY_CODE_ISSUE = 'not_supported_country_code_issue',
    RECENT_HOME_CONTENT_QUESTIONNAIRE_ANSWERED_ATTEMPT = 'recent_home_content_questionnaire_answered_attempt',
    MISMATCH_PAGE = 'mismatch_page',
    ILLEGAL_ARGUMENT = 'illegal_argument',
    METHOD_ARGUMENT_NOT_VALID = 'method_argument_not_valid',
    TRANSLATION_NOT_FOUND = 'translation_not_found',
    INVALID_DIRECTUS_STATE = 'invalid_directus_state',
    GRAPHQL_VALIDATION_ERROR = 'graphql_validation_error',
    INTERNAL_APOLLO_EXCEPTION = 'internal_apollo_exception',
    APOLLO_NETWORK_ERROR = 'apollo_network_error',
    // Insurance verification — the backend sends these uppercase (see InsuranceVerificationController)
    INVALID_MEMBER_ID = 'INVALID_MEMBER_ID',
    COVERAGE_NOT_FOUND = 'COVERAGE_NOT_FOUND',
    VENDOR_UNAVAILABLE = 'VENDOR_UNAVAILABLE',
    VENDOR_TIMEOUT = 'VENDOR_TIMEOUT',
    VERIFICATION_NOT_FOUND = 'VERIFICATION_NOT_FOUND',
    COVERAGE_NOT_ACTIVE = 'COVERAGE_NOT_ACTIVE',
}

/** Client-side error codes generated by the application itself */
export enum AppErrorCode {
    NETWORK_ERROR = 'network_error',
    REQUEST_ABORTED = 'request_aborted',
    TOKEN_REFRESH_REJECTED = 'token_refresh_rejected',
    SESSION_EXPIRED = 'session_expired',
    UNKNOWN = 'unknown',
}

/** DTO type for ErrorCode */
export type ErrorCode = AppErrorCode | ErrorCodesDto;

/**
 * Error codes the backend returns for expected business outcomes rather than faults: a rule the UI
 * is built to handle, or input the user can correct. They still reject so the caller can react, but
 * reporting them to Sentry fills the alert channels with flows that work as designed. Anything not
 * listed here is treated as a fault and reported.
 *
 * The predicate is given the code *after* errorCodeMapping, so a mapping entry silences its source
 * code too even though it is not written here — today NOT_SUPPORTED_COUNTRY_CODE_ISSUE and
 * RECENT_HOME_CONTENT_QUESTIONNAIRE_ANSWERED_ATTEMPT arrive that way. errorCodeMapping exists to
 * reuse translations, so adding an entry that lands on a code below stops reporting it as a side
 * effect: check that is what you want. errors.test.ts pins the two that come in this way.
 *
 * Deliberately absent: PASSWORD_CHANGE_ISSUE, a catch-all for whatever the identity provider
 * rejected rather than something the user did; and the captcha codes, since a captcha breaking is a
 * signal worth having.
 */
export const expectedErrorCodes: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  // Zitadel answered invalid_grant: the refresh token is spent, so the session ends and the user
  // is logged out with a notice. Ordinary end of session, not a fault. Other OAuth errors
  // (invalid_scope, invalid_client) are configuration defects and keep alerting.
  AppErrorCode.TOKEN_REFRESH_REJECTED,
  // The user navigated away mid-request. Some browsers surface that as an AbortError, which is
  // someone leaving the page rather than anything failing.
  AppErrorCode.REQUEST_ABORTED,
  // Access rules the UI answers with a redirect or a dedicated screen
  ErrorCodesDto.NO_ACTIVE_LICENSES,
  ErrorCodesDto.CONSENT_REQUIRED,
  ErrorCodesDto.CONSENT_NEEDS_REAPPROVAL,
  ErrorCodesDto.ACTIVE_SUBSCRIPTION_EXISTS,
  // Completing a Google sign-up for an identity the auth server no longer has, one whose address
  // was never verified, an address another account already holds, or an identity that already has
  // its own account — each answered with a fresh sign-in, a message, or by letting the user in,
  // none of them a fault (IPD-2844). CONFLICT stays absent: it is the generic 409 of every
  // endpoint, so silencing it here would silence every other endpoint's too. The already-registered
  // case it used to carry has its own code above, which is what makes it silenceable (IPD-3078).
  ErrorCodesDto.AUTH_SERVER_USER_NOT_FOUND,
  ErrorCodesDto.AUTH_SERVER_EMAIL_UNVERIFIED,
  ErrorCodesDto.EMAIL_ALREADY_REGISTERED,
  ErrorCodesDto.USER_ALREADY_REGISTERED,
  // User input the form reports back in place
  ErrorCodesDto.DUPLICATED_EMAIL,
  ErrorCodesDto.INVALID_VOUCHER_CODE,
  ErrorCodesDto.VOUCHER_RENEWAL_INVALID,
  ErrorCodesDto.INVALID_MARKETING_CODE,
  ErrorCodesDto.INVALID_VERIFICATION_CODE,
  ErrorCodesDto.INVALID_PASSWORD,
  ErrorCodesDto.PASSWORD_POLICY_VIOLATION,
  ErrorCodesDto.INVALID_AGE,
  ErrorCodesDto.INVALID_COUNTRY,
  ErrorCodesDto.MULTIPLE_QUESTIONNAIRE_ANSWERING_ATTEMPT,
  // Outcomes of an insurance lookup. VENDOR_UNAVAILABLE and VENDOR_TIMEOUT stay reported:
  // those are the vendor failing, not an answer about the member.
  ErrorCodesDto.INVALID_MEMBER_ID,
  ErrorCodesDto.COVERAGE_NOT_FOUND,
  ErrorCodesDto.COVERAGE_NOT_ACTIVE,
  ErrorCodesDto.VERIFICATION_NOT_FOUND,
]);

/** Whether an error code is an expected business outcome and must not be reported to Sentry. */
export function isExpectedErrorCode(code: string): boolean {
  return expectedErrorCodes.has(code as ErrorCode);
}

/** Maps backend error codes to their translation keys (for codes that reuse existing translations) */
export const errorCodeMapping: Record<string, ErrorCodesDto | AppErrorCode> = {
   
  // User-facing errors mapped to existing translations
  [ErrorCodesDto.NOT_SUPPORTED_COUNTRY_CODE_ISSUE]: ErrorCodesDto.INVALID_COUNTRY,
  [ErrorCodesDto.RECENT_HOME_CONTENT_QUESTIONNAIRE_ANSWERED_ATTEMPT]: ErrorCodesDto.MULTIPLE_QUESTIONNAIRE_ANSWERING_ATTEMPT,
  [ErrorCodesDto.MISMATCH_PAGE]: ErrorCodesDto.MISMATCH_SESSION,
  // Zitadel's OAuth shape: a spent refresh token ends the session.
  invalid_grant: AppErrorCode.TOKEN_REFRESH_REJECTED, // eslint-disable-line @typescript-eslint/naming-convention
  // Technical/internal errors mapped to generic translations
  [ErrorCodesDto.ILLEGAL_ARGUMENT]: ErrorCodesDto.BAD_REQUEST,
  [ErrorCodesDto.METHOD_ARGUMENT_NOT_VALID]: ErrorCodesDto.BAD_REQUEST,
  [ErrorCodesDto.TRANSLATION_NOT_FOUND]: ErrorCodesDto.RESOURCE_NOT_FOUND,
  [ErrorCodesDto.INVALID_DIRECTUS_STATE]: ErrorCodesDto.BAD_REQUEST,
  [ErrorCodesDto.GRAPHQL_VALIDATION_ERROR]: ErrorCodesDto.BAD_REQUEST,
  [ErrorCodesDto.INTERNAL_APOLLO_EXCEPTION]: ErrorCodesDto.BAD_REQUEST,
  [ErrorCodesDto.APOLLO_NETWORK_ERROR]: AppErrorCode.NETWORK_ERROR,
   
};
